import './RewardsSearchResultList.scss';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ROUTE_REWARDS_V2_STORE_ID,
  STORE_ID_ROUTE_PARAM,
} from '../../config/routes';
import IconCircled, { ICON_CIRCLE_SIZE } from '../IconCircled';
import TCPill, { TC_PILL_TYPE } from '../TCPill';
import DealSvg from '../SvgComponents/Deal_svg';
import DotsLoader from '../DotsLoader';
import {
  getTrackerId,
  getActionType,
  POSTHOG_CAPTURE_ATTRIBUTES,
  TRACKER_IDS,
} from '../../config/tracker';

const { rewards: { list: { storeItem: trackerStoreItem } } } = TRACKER_IDS;

const RewardsSearchResultList = ({
  brandsList = [],
  loadMore,
  isLoadingRewards,
  hasMore,
  error,
  firstLoad,
  searchedText,
}) => {
  const history = useHistory();

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle === 'hidden' ? 'visible' : originalStyle;
    };
  }, []);

  const handleRowClick = useCallback((e) => {
    const { brandId } = e.currentTarget.dataset;
    history.push(ROUTE_REWARDS_V2_STORE_ID.replace(STORE_ID_ROUTE_PARAM, brandId));
  }, [history]);

  const handleRowKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') handleRowClick(e);
  }, [handleRowClick]);

  if (isLoadingRewards && firstLoad) {
    return <DotsLoader />;
  }

  if (error) {
    return (
      <div className="RewardsSearchResultList__error">
        {error}
      </div>
    );
  }

  if (
    !isLoadingRewards &&
    brandsList.length === 0 &&
    searchedText && !firstLoad
  ) {
    return (
      <div className="RewardsSearchResultList__zero-state">
        <div className="RewardsSearchResultList__zero-title">Welp, no brands were found</div>
        <div className="RewardsSearchResultList__zero-desc">Try again with a different term.</div>
      </div>
    );
  }

  return (
    <div className="RewardsSearchResultList">
      <div
        id="rewards-scroll-container"
        className="RewardsSearchResultList__scroll-container"
      >
        <InfiniteScroll
          dataLength={brandsList.length}
          next={loadMore}
          hasMore={hasMore}
          scrollableTarget="rewards-scroll-container"
        >
          {brandsList.map((brand, index) => {
            const firstItem = brand.items?.[0];
            const cardBgColor = firstItem?.card_bg_color;
            const tcCreditAmount = firstItem?.tc_credit_amount;
            const tcCreditAmountType = firstItem?.tc_credit_amount_type;

            return (
              <div
                className="RewardsSearchResultList__row"
                key={brand.brand_id}
                data-brand-id={brand.brand_id}
                onClick={handleRowClick}
                role="button"
                tabIndex={0}
                aria-label={`View deals for ${brand.brand_name}`}
                onKeyDown={handleRowKeyDown}
                {...{
                  [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerStoreItem),
                  [POSTHOG_CAPTURE_ATTRIBUTES.ACTION_ID]: getActionType(trackerStoreItem),
                  [POSTHOG_CAPTURE_ATTRIBUTES.STORE_ID]: brand.brand_id,
                  [POSTHOG_CAPTURE_ATTRIBUTES.STORE_NAME]: brand.brand_name,
                  [POSTHOG_CAPTURE_ATTRIBUTES.LIST_POSITION]: index + 1,
                }}
              >
                <IconCircled
                  className="RewardsSearchResultList__icon"
                  icon={<img src={brand.logo_url} alt={`${brand.brand_name} logo`} />}
                  size={ICON_CIRCLE_SIZE.MEDIUM}
                  backgroundColor={cardBgColor}
                />
                <div className="RewardsSearchResultList__info">
                  {brand.total_items > 0 && (
                    <div className="RewardsSearchResultList__deals-pill">
                      <DealSvg width={10} height={10} />
                      <span>{brand.total_items === 1 ? '1 Deal' : `${brand.total_items} Deals`}</span>
                    </div>
                  )}
                  <div className="RewardsSearchResultList__brand">{brand.brand_name}</div>
                </div>
                {Number(tcCreditAmount) > 0 && (
                  <TCPill
                    balance={Number(tcCreditAmount)}
                    type={TC_PILL_TYPE.EARN}
                    amountType={tcCreditAmountType}
                  />
                )}
              </div>
            );
          })}
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default RewardsSearchResultList;
